// src/pages/Website.js
// Management-only CMS for the public website: add/edit/remove the
// Authors & Books shown on the home page, and see how many people
// have viewed the site.
import React, { useEffect, useState } from 'react';
import { T, Card, Btn, StatCard, SectionTitle, Modal, Spinner, Empty, LBL, INP } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import { listWebsiteBooks, createWebsiteBook, updateWebsiteBook, deleteWebsiteBook, getWebsiteStats } from '../services/api';

export default function Website() {
  const { can } = useAuth();
  const [books, setBooks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=closed, {}=new, {...book}=edit
  const [msg, setMsg] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([listWebsiteBooks(), getWebsiteStats()])
      .then(([b, s]) => { setBooks(b.data.data); setStats(s.data.data); })
      .catch(() => setMsg({ type: 'error', text: 'Failed to load website data.' }))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (!can('manage_website')) {
    return <Empty msg="You don't have access to website management." />;
  }

  const handleDelete = async (b) => {
    if (!window.confirm(`Remove "${b.title}" from the website?`)) return;
    try {
      await deleteWebsiteBook(b.id);
      load();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to remove book.' });
    }
  };

  const homeViews = stats?.byPage?.find(p => p.page === 'home')?.views ?? 0;

  return (
    <div>
      <SectionTitle sub="Manage the Authors & Books shown on the public home page, and see how many people have visited.">Website</SectionTitle>

      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <StatCard label="Home Page Views" value={homeViews.toLocaleString()} sub="All-time visits" color={T.gold} />
        <StatCard label="Books Listed" value={books.length} sub="Total in CMS" />
        <StatCard label="Live on Website" value={books.filter(b => b.is_active).length} sub="Visible to visitors" color={T.success} />
      </div>

      {msg && <div style={{ color: msg.type === 'error' ? T.danger : T.success, fontSize: '13px', marginBottom: '14px' }}>{msg.text}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
        <Btn onClick={() => setEditing({})}>+ Add author / book</Btn>
      </div>

      {loading ? <Spinner /> : books.length === 0 ? (
        <Empty msg="No books yet. Add your first author & book to show on the website." />
      ) : (
        <Card style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>Title</th>
                <th style={TH}>Author</th>
                <th style={TH}>Year</th>
                <th style={TH}>Featured</th>
                <th style={TH}>Visible</th>
                <th style={TH}></th>
              </tr>
            </thead>
            <tbody>
              {books.map(b => (
                <tr key={b.id} style={{ opacity: b.is_active ? 1 : 0.45 }}>
                  <td style={TD}><b>{b.title}</b></td>
                  <td style={TD}>{b.author_name}</td>
                  <td style={TD}>{b.published_year || '—'}</td>
                  <td style={TD}>{b.is_featured ? '★ Yes' : '—'}</td>
                  <td style={TD}>{b.is_active ? 'Yes' : 'Hidden'}</td>
                  <td style={TD}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Btn size="sm" variant="outline" onClick={() => setEditing(b)}>Edit</Btn>
                      <Btn size="sm" variant="danger" onClick={() => handleDelete(b)}>Remove</Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {editing !== null && (
        <BookModal
          book={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function BookModal({ book, onClose, onSaved }) {
  const isNew = !book.id;
  const [form, setForm] = useState({
    title: book.title || '',
    author_name: book.author_name || '',
    description: book.description || '',
    cover_image_url: book.cover_image_url || '',
    published_year: book.published_year || '',
    is_featured: !!book.is_featured,
    is_active: book.is_active !== undefined ? !!book.is_active : true,
  });
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setMsg(null);
    if (!form.title.trim()) return setMsg('Book title is required.');
    if (!form.author_name.trim()) return setMsg('Author name is required.');

    const payload = {
      title: form.title.trim(),
      author_name: form.author_name.trim(),
      description: form.description,
      cover_image_url: form.cover_image_url,
      published_year: form.published_year ? Number(form.published_year) : null,
      is_featured: form.is_featured,
      is_active: form.is_active,
    };

    setLoading(true);
    try {
      if (isNew) await createWebsiteBook(payload);
      else await updateWebsiteBook(book.id, payload);
      onSaved();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to save.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={isNew ? 'Add author / book' : `Edit "${book.title}"`} onClose={onClose}>
      <label style={LBL}>Book title</label>
      <input style={{ ...INP, marginBottom: '12px' }} value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Beyond the Horizon" />

      <label style={LBL}>Author name</label>
      <input style={{ ...INP, marginBottom: '12px' }} value={form.author_name} onChange={e => set('author_name', e.target.value)} placeholder="e.g. Jane Wanjiru" />

      <label style={LBL}>Description (optional)</label>
      <input style={{ ...INP, marginBottom: '12px' }} value={form.description} onChange={e => set('description', e.target.value)} placeholder="A short blurb shown on the website" />

      <label style={LBL}>Cover image URL (optional)</label>
      <input style={{ ...INP, marginBottom: '12px' }} value={form.cover_image_url} onChange={e => set('cover_image_url', e.target.value)} placeholder="https://..." />

      <label style={LBL}>Published year (optional)</label>
      <input style={{ ...INP, marginBottom: '12px' }} type="number" value={form.published_year} onChange={e => set('published_year', e.target.value)} placeholder="e.g. 2025" />

      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '10px', cursor: 'pointer' }}>
        <input type="checkbox" checked={form.is_featured} onChange={e => set('is_featured', e.target.checked)} />
        Feature this book at the top of the website
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '16px', cursor: 'pointer' }}>
        <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
        Visible on the public website
      </label>

      {msg && <div style={{ color: T.danger, fontSize: '12px', marginBottom: '12px' }}>{msg}</div>}

      <Btn onClick={submit} disabled={loading}>{loading ? 'Saving...' : (isNew ? 'Add to website' : 'Save changes')}</Btn>
    </Modal>
  );
}

const TH = { textAlign: 'left', padding: '10px 14px', fontSize: '11px', color: T.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `1px solid ${T.border}` };
const TD = { padding: '11px 14px', fontSize: '13px', borderBottom: `1px solid ${T.surface}` };
